/**
 * @file CustomComponentSelect.tsx
 * @module app/custom/Select/CustomComponentSelect
 * @description Renders the CustomComponentSelect reusable select control, including option presentation, creation, multi-selection, and table-filter behavior.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

/* eslint-disable jsx-a11y/anchor-is-valid */
import Select, { components } from 'react-select';
const CustomComponentSelect = (props: any) => {
  const InputOption = ({
    getStyles,
    Icon,
    isDisabled,
    isFocused,
    isSelected,
    children,
    innerProps,
    ...rest
  }: any) => {
    return (
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isFocused={isFocused}
        isSelected={isSelected}
        getStyles={getStyles}
        innerProps={innerProps}
      >
        <div className="form-check form-check-custom form-check-solid form-check-lg">
          <input
            className="form-check-input me-4"
            type="checkbox"
            value=""
            checked={isSelected}
            readOnly
          />
          {children}
        </div>
      </components.Option>
    );
  };
  const customFilterOption = (option: any, rawInput: any) => {
    const label = option?.data?.title;
    const input = rawInput?.toLowerCase();
    const text = label?.toLowerCase();
    return text?.includes(input);
  };
  // Comma-separated tooltip of all selected names, since the collapsed tag view can't show everything.
  const selectedValues = Array.isArray(props.value)
    ? props.value
    : props.value
    ? [props.value]
    : [];
  const selectedNamesTitle = selectedValues
    .map((v: any) => v?.name || v?.label)
    .filter(Boolean)
    .join(', ');
  return (
    <>
      <div title={selectedNamesTitle}>
      <Select
        placeholder={props.placeholder}
        value={props.value}
        onMenuOpen={props.onMenuOpen}
        onMenuClose={props.onMenuClose}
        closeMenuOnSelect={props.closeMenuOnSelect}
        hideSelectedOptions={props.hideSelectedOptions}
        defaultValue={props.defaultValue}
        options={props.options}
        isSearchable={props.isSearchable}
        getOptionLabel={props.getOptionLabel}
        filterOption={customFilterOption}
        styles={{
          option: (base:any) => ({
            ...base,
            border: `0.5px solid #f9f9f9`,
            margin: '0px',
            background: 'white',
            padding: '1rem 1.25rem',
            ':active': {
              ...base[':active'],
              color: '#1b74e4',
              background: '#f1faff',
            },
            ':hover': {
              ...base[':active'],
              color: '#1b74e4',
              background: '#f1faff',
            },
            color: '#5e6278',
            fontSize: '16px',
            fontWeight: '600',
          }),
          control: (base:any) => ({
            ...base,
            border: `0.5px solid #e0e0df`,
            background: '#ffff',
            boxShadow: 'none',
            minHeight: '40px',
            minWidth: '300px',
            borderRadius: '8px',
            padding: '6.5px 20.4px 6.5px 10px',
            fontSize: '16px',
            fontWeight: '600',
          }),
          valueContainer: (base:any) => ({
            ...base,
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }),
          multiValue: (base:any) => {
            return {
              ...base,
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '6px',
              backgroundColor: '#e7f1fd',
              padding: '8px 8px 8px 10px',
              color: '#1a1a1a',
            };
          },
          menu: (base:any) => ({
            ...base,
            // width: '300px',
          }),
          indicatorSeparator: (base:any) => ({
            ...base,
            background: '#f9f9f9',
          }),
          indicatorsContainer: (base:any) => ({
            ...base,
            color: '#f9f9f9',
            strokeWidth: '5px',
          }),
          multiValueRemove: (base:any, { data }:any) => ({
            ...base,
            fontSize: '16px',
            fontWeight: '600',
            svg: {
              width: '20px',
              height: '20px',
              color: '#7c7c7c',
            },
            ':hover': {
              backgroundColor: '#e7f1fd',
            },
          }),
        }}
        onChange={props.onChange}
        onInputChange={props.onInputChange}
        onMenuScrollToBottom={props.onMenuScrollToBottom}
        isDisabled={props.isDisabled ? props.isDisabled : false}
        isLoading = {props.isLoading}
        isMulti={props.isMulti}
        components={{
          Option: InputOption,
          MultiValueContainer: ({ selectProps, data }:any) => {
            const values = selectProps.value;
            if (values) {
              let name_ =
                values[values.length - 1].id === data.id
                  ? values.length === 1
                    ? data.label
                    : data.name
                  : data.name + ', ';
              return values[values.length - 1].id === data.id ? (
                values.length === 1 ? (
                  name_
                ) : (
                  ''
                )
              ) : (
                <div className="d-flex align-items-center">
                  {values[values.length - values.length].id === data.id ? (
                    <div className="symbol symbol-40px symbol-circle me-3">
                      <div className="symbol-label fs-20 fw-600 bg-light-primary text-primary">
                        {values.length}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                  <h6 className="fs-16 fw-600 text-black mb-0">
                    {name_.length > 10
                      ? values[values.length - values.length].id === data.id
                        ? name_.substring(0, 10) + '...'
                        : ''
                      : name_}
                  </h6>
                </div>
              );
            } else return '';
          },
        }}
      />
      </div>
    </>
  );
};
export { CustomComponentSelect };
